import React, { useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaClock,
  FaEdit,
  FaMapMarkerAlt,
  FaSearch,
  FaShieldAlt,
  FaSyncAlt,
  FaTrashAlt,
  FaTv,
  FaUnlink,
} from "react-icons/fa";
import TvEmptyState from "../../components/tv-disply/TvEmptyState";
import ConfirmModal from "../../components/ConfirmModal";
import {
  approveTvPairing,
  deleteTvDevice,
  disableTvDevice,
  enableTvDevice,
  rejectTvPairing,
  renameTvDevice,
  revokeTvDevice,
  updateTvDeviceDetails,
} from "../../services/tv-disply/tvDeviceService";
import {
  TV_DEVICE_STATUSES,
  TV_PAIRING_STATUSES,
} from "../../services/tv-disply/tvDisplayConstants";
import {
  getComputedDeliveryStatus,
  unassignTvPlaylist,
} from "../../services/tv-disply/tvPublishService";

const DEVICE_FILTERS = ["ALL", "ONLINE", "OFFLINE", "ENABLED", "DISABLED", "REVOKED"];
const INITIAL_APPROVAL_FORM = { deviceName: "", country: "", state: "", city: "", notes: "" };
const INITIAL_EDIT_FORM = { deviceName: "", country: "", state: "", city: "", notes: "" };

export default function TvDevices({
  deviceRows = [],
  deviceLoading = false,
  deviceError = "",
  pairingRows = [],
  pairingLoading = false,
  pairingError = "",
  deviceStats,
  pairingStats,
}) {
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("ALL");
  const [pairingCodeSearch, setPairingCodeSearch] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveForm, setApproveForm] = useState(INITIAL_APPROVAL_FORM);
  const [viewTarget, setViewTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const filteredDevices = useMemo(() => {
    const query = deviceSearch.trim().toLowerCase();
    return deviceRows.filter((device) => {
      const matchesQuery = !query || device.searchText.includes(query);
      if (!matchesQuery) return false;

      if (deviceFilter === "ONLINE") return device.computedStatus === TV_DEVICE_STATUSES.ONLINE;
      if (deviceFilter === "OFFLINE") return device.computedStatus === TV_DEVICE_STATUSES.OFFLINE;
      if (deviceFilter === "ENABLED") return device.enabled !== false && device.pairingStatus !== TV_PAIRING_STATUSES.REVOKED;
      if (deviceFilter === "DISABLED") return device.computedStatus === TV_DEVICE_STATUSES.DISABLED;
      if (deviceFilter === "REVOKED") return device.pairingStatus === TV_PAIRING_STATUSES.REVOKED;
      return true;
    });
  }, [deviceFilter, deviceRows, deviceSearch]);

  const pairingLookup = useMemo(() => {
    const query = pairingCodeSearch.trim().toUpperCase();
    if (!query) return null;
    return pairingRows.find((pairing) => pairing.pairingCode === query) || null;
  }, [pairingCodeSearch, pairingRows]);

  const pendingPairings = useMemo(
    () => pairingRows.filter((pairing) => pairing.status === TV_PAIRING_STATUSES.REQUESTED || pairing.status === TV_PAIRING_STATUSES.EXPIRED),
    [pairingRows]
  );

  async function runAction(actionKey, action, successMessage) {
    setBusyKey(actionKey);
    setError("");
    setMessage("");
    try {
      await action();
      setMessage(successMessage);
    } catch (actionError) {
      setError(resolveActionError(actionError));
    } finally {
      setBusyKey("");
    }
  }

  function openApprove(pairing) {
    setApproveTarget(pairing);
    setApproveForm({
      deviceName: pairing.deviceName || "",
      country: "",
      state: "",
      city: "",
      notes: "",
    });
    setError("");
    setMessage("");
  }

  function openEdit(device) {
    setEditTarget(device);
    setEditForm({
      deviceName: device.deviceName || "",
      country: device.country || "",
      state: device.state || "",
      city: device.city || "",
      notes: device.notes || "",
    });
    setError("");
    setMessage("");
  }

  async function confirmDeleteDevice() {
    if (!deleteTarget?.deviceId || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError("");
    setError("");
    setMessage("");
    try {
      const deletedId = deleteTarget.deviceId;
      await deleteTvDevice(deletedId);
      setDeleteTarget(null);
      setMessage(`TV ${deletedId} deleted. The realtime registry has been updated.`);
    } catch (actionError) {
      setDeleteError(resolveActionError(actionError));
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <section className="tv-display-panel">
      <div className="tv-display-panel-head">
        <div>
          <h2 className="tv-display-panel-title">TV Devices & Pairing</h2>
          <p className="tv-display-panel-copy">
            Pairing requests are admin-approved in realtime, every TV gets its own Firebase identity,
            and device actions stay behind secure callable functions.
          </p>
        </div>
      </div>

      <div className="tv-display-media-kpi-grid is-five">
        <MetricCard label="Total TVs" value={deviceLoading ? "..." : String(deviceStats?.total || 0)} />
        <MetricCard label="Online" value={deviceLoading ? "..." : String(deviceStats?.online || 0)} />
        <MetricCard label="Offline" value={deviceLoading ? "..." : String(deviceStats?.offline || 0)} />
        <MetricCard label="Disabled" value={deviceLoading ? "..." : String(deviceStats?.disabled || 0)} />
        <MetricCard label="Pending Pairings" value={pairingLoading ? "..." : String(pairingStats?.pending || 0)} />
      </div>

      {message ? <div className="tv-display-feedback is-success">{message}</div> : null}
      {error ? <div className="tv-display-feedback is-error">{error}</div> : null}
      {deviceError ? <div className="tv-display-feedback is-error">{deviceError}</div> : null}
      {pairingError ? <div className="tv-display-feedback is-error">{pairingError}</div> : null}

      <div className="tv-display-device-panels">
        <div className="tv-display-device-section">
          <div className="tv-display-device-section-head">
            <div>
              <div className="tv-display-section-kicker">Pending Pairings</div>
              <h3 className="tv-display-device-section-title">Approve TVs by pairing code</h3>
            </div>
            <div className="tv-display-device-section-meta">
              {pairingLoading ? "Syncing..." : `${pairingStats?.pending || 0} waiting approval`}
            </div>
          </div>

          <div className="tv-display-pairing-lookup">
            <label className="tv-display-field">
              <span className="tv-display-field-label">Enter TV Pairing Code</span>
              <div className="tv-display-search-shell is-code">
                <FaSearch />
                <input
                  value={pairingCodeSearch}
                  onChange={(event) => setPairingCodeSearch(event.target.value.toUpperCase())}
                  placeholder="A7K3P9"
                  maxLength={12}
                />
              </div>
            </label>

            <div className="tv-display-pairing-lookup-result">
              {!pairingCodeSearch.trim() ? (
                <span>Find a specific TV request by entering the pairing code shown on the TV.</span>
              ) : pairingLookup ? (
                <PairingLookupMessage pairing={pairingLookup} />
              ) : (
                <span>Pairing code not found.</span>
              )}
            </div>
          </div>

          {pairingLoading ? (
            <div className="tv-display-feedback is-loading">Loading pairing requests from Firestore.</div>
          ) : pendingPairings.length ? (
            <div className="tv-display-pairing-grid">
              {pendingPairings.map((pairing) => {
                const actionKeyBase = `pairing:${pairing.id}`;
                const isPending = pairing.status === TV_PAIRING_STATUSES.REQUESTED;
                return (
                  <article key={pairing.id} className="tv-display-pairing-card">
                    <div className="tv-display-pairing-card-top">
                      <div>
                        <div className="tv-display-pairing-code">{pairing.pairingCode}</div>
                        <div className="tv-display-pairing-status-line">
                          <StatusChip value={pairing.status} />
                          <span>{formatRelative(pairing.createdAt)}</span>
                        </div>
                      </div>
                      <div className="tv-display-pairing-expiry">
                        <FaClock />
                        <span>{pairing.isExpired ? "Expired" : `Expires ${formatRelative(pairing.expiresAt)}`}</span>
                      </div>
                    </div>

                    <div className="tv-display-pairing-meta">
                      <div><strong>Device:</strong> {pairing.requestedDeviceInfo.manufacturer || "Unknown"} / {pairing.requestedDeviceInfo.model || "Android TV"}</div>
                      <div><strong>App:</strong> {pairing.requestedDeviceInfo.appVersion || "Unknown"}</div>
                      <div><strong>Android:</strong> {pairing.requestedDeviceInfo.androidVersion || "Unknown"}</div>
                    </div>

                    <div className="tv-display-card-actions">
                      <button
                        type="button"
                        className="tv-display-action-btn is-primary"
                        onClick={() => openApprove(pairing)}
                        disabled={!isPending || busyKey === `${actionKeyBase}:approve`}
                      >
                        <FaCheckCircle />
                        Approve
                      </button>
                      <button
                        type="button"
                        className="tv-display-action-btn is-danger"
                        onClick={() => {
                          if (!isPending) return;
                          if (!window.confirm(`Reject pairing code ${pairing.pairingCode}?`)) return;
                          runAction(
                            `${actionKeyBase}:reject`,
                            () => rejectTvPairing({ pairingCode: pairing.pairingCode }),
                            `Pairing ${pairing.pairingCode} rejected.`
                          );
                        }}
                        disabled={!isPending || busyKey === `${actionKeyBase}:reject`}
                      >
                        <FaUnlink />
                        Reject
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <TvEmptyState
              title="No pending pairings"
              copy="New TV requests will appear here in realtime as soon as an unpaired TV asks for a code."
            />
          )}
        </div>

        <div className="tv-display-device-section">
          <div className="tv-display-device-section-head">
            <div>
              <div className="tv-display-section-kicker">Devices</div>
              <h3 className="tv-display-device-section-title">Paired TV registry</h3>
            </div>
            <div className="tv-display-device-section-meta">
              {deviceLoading ? "Syncing..." : `${filteredDevices.length} of ${deviceRows.length} shown`}
            </div>
          </div>

          <div className="tv-display-device-toolbar">
            <label className="tv-display-search-shell">
              <FaSearch />
              <input
                value={deviceSearch}
                onChange={(event) => setDeviceSearch(event.target.value)}
                placeholder="Search device ID, name, city, state, country"
              />
            </label>

            <div className="tv-display-filter-row">
              {DEVICE_FILTERS.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={`tv-display-filter-chip ${deviceFilter === filter ? "is-active" : ""}`}
                  onClick={() => setDeviceFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {deviceLoading ? (
            <div className="tv-display-feedback is-loading">Loading registered TV devices from Firestore.</div>
          ) : filteredDevices.length ? (
            <div className="tv-display-device-list">
              {filteredDevices.map((device) => {
                const actionKeyBase = `device:${device.id}`;
                const isLiveConnected = device.computedStatus === TV_DEVICE_STATUSES.ONLINE;
                return (
                  <article key={device.id} className={`tv-display-device-record ${isLiveConnected ? "is-live-connected" : "is-not-connected"}`}>
                    <div className={`tv-device-live-banner ${isLiveConnected ? "is-online" : "is-offline"}`}>
                      <span className="tv-device-live-dot" />
                      <strong>{isLiveConnected ? "LIVE CONNECTED" : "NOT CONNECTED"}</strong>
                      <span>{isLiveConnected ? "Heartbeat received within 2 minutes" : `Last heartbeat: ${formatDateTime(device.lastSeenAt)}`}</span>
                    </div>
                    <div className="tv-display-device-record-head">
                      <div className="tv-display-device-record-title">
                        <div className="tv-display-device-icon">
                          <FaTv />
                        </div>
                        <div>
                          <div className="tv-display-device-name">{device.deviceName || "Unnamed TV Device"}</div>
                          <div className="tv-display-device-id">{device.deviceId}</div>
                        </div>
                      </div>
                      <div className="tv-display-device-badges">
                        <StatusChip value={isLiveConnected ? "ONLINE" : "OFFLINE"} tone={isLiveConnected ? "success" : "danger"} />
                        <StatusChip value={device.enabled === false ? "DISABLED" : "ENABLED"} tone={device.enabled === false ? "danger" : "success"} />
                        <StatusChip value={device.pairingStatus} tone={device.pairingStatus === TV_PAIRING_STATUSES.REVOKED ? "danger" : "muted"} />
                      </div>
                    </div>

                    <div className="tv-display-device-record-grid">
                      <DeviceMeta label="Location" value={formatLocation(device)} icon={<FaMapMarkerAlt />} />
                      <DeviceMeta label="Hardware" value={formatHardware(device)} />
                      <DeviceMeta label="Android" value={device.androidVersion || "Unknown"} />
                      <DeviceMeta label="App Version" value={device.appVersion || "Unknown"} />
                      <DeviceMeta label="Last Seen" value={formatDateTime(device.lastSeenAt)} />
                      <DeviceMeta label="Registered" value={formatDateTime(device.registeredAt)} />
                      <DeviceMeta label="Assigned Playlist" value={device.assignedPlaylistName || device.assignedPlaylistId || "Not Assigned"} />
                      <DeviceMeta label="Published" value={device.lastPublishedVersion ? `Version ${device.lastPublishedVersion}` : "Never"} />
                      <DeviceMeta label="Applied" value={device.lastAppliedVersion != null ? `Version ${device.lastAppliedVersion}` : "Waiting"} />
                      <DeviceMeta label="Delivery" value={getComputedDeliveryStatus(device)} />
                      <DeviceMeta label="Signage Mode" value={device.signageMode || "STANDARD"} />
                      <DeviceMeta label="Playback Health" value={device.playbackHealth || "STARTING"} />
                      <DeviceMeta label="Kiosk" value={device.isLockTaskActive ? "Active" : "Inactive"} />
                      <DeviceMeta label="Home Mode" value={device.isManagedHome ? "Active" : "Inactive"} />
                      <DeviceMeta label="Auth UID" value={device.authUid || "Pending"} />
                    </div>

                    <div className="tv-display-card-actions is-wrap">
                      <button
                        type="button"
                        className="tv-display-action-btn"
                        onClick={() => setViewTarget(device)}
                      >
                        <FaShieldAlt />
                        View
                      </button>
                      <button
                        type="button"
                        className="tv-display-action-btn"
                        onClick={async () => {
                          const nextName = window.prompt("Enter new device name", device.deviceName || "");
                          if (nextName == null) return;
                          runAction(
                            `${actionKeyBase}:rename`,
                            () => renameTvDevice(device.deviceId, nextName),
                            `Device ${device.deviceId} renamed.`
                          );
                        }}
                        disabled={busyKey === `${actionKeyBase}:rename`}
                      >
                        <FaEdit />
                        Rename
                      </button>
                      <button
                        type="button"
                        className="tv-display-action-btn"
                        onClick={() => openEdit(device)}
                      >
                        <FaEdit />
                        Edit Details
                      </button>
                      <button
                        type="button"
                        className="tv-display-action-btn"
                        onClick={() => {
                          const enableAction = device.enabled === false ? enableTvDevice : disableTvDevice;
                          const label = device.enabled === false ? "enable" : "disable";
                          if (!window.confirm(`Are you sure you want to ${label} ${device.deviceName || device.deviceId}?`)) return;
                          runAction(
                            `${actionKeyBase}:${label}`,
                            () => enableAction(device.deviceId),
                            `Device ${device.deviceId} ${label}d.`
                          );
                        }}
                        disabled={
                          device.pairingStatus === TV_PAIRING_STATUSES.REVOKED ||
                          busyKey === `${actionKeyBase}:enable` ||
                          busyKey === `${actionKeyBase}:disable`
                        }
                      >
                        <FaSyncAlt />
                        {device.enabled === false ? "Enable" : "Disable"}
                      </button>
                      <button
                        type="button"
                        className="tv-display-action-btn is-danger"
                        onClick={() => {
                          if (!window.confirm(`Unassign the current playlist from ${device.deviceName || device.deviceId}?`)) return;
                          runAction(
                            `${actionKeyBase}:unassign`,
                            () => unassignTvPlaylist(device.deviceId),
                            `Device ${device.deviceId} unassigned from published TV content.`
                          );
                        }}
                        disabled={busyKey === `${actionKeyBase}:unassign`}
                      >
                        <FaUnlink />
                        Unassign Playlist
                      </button>
                      <button
                        type="button"
                        className="tv-display-action-btn is-danger"
                        onClick={() => {
                          const confirmText = "Revoke this TV?\n\nThe TV will lose authorization and must be paired again before it can receive future advertising updates.";
                          if (!window.confirm(confirmText)) return;
                          runAction(
                            `${actionKeyBase}:revoke`,
                            () => revokeTvDevice(device.deviceId),
                            `Device ${device.deviceId} revoked. Re-pairing is now required.`
                          );
                        }}
                        disabled={busyKey === `${actionKeyBase}:revoke`}
                      >
                        <FaUnlink />
                        Revoke Pairing
                      </button>
                      <button
                        type="button"
                        className="tv-display-action-btn is-danger"
                        onClick={() => {
                          setDeleteTarget(device);
                          setDeleteError("");
                        }}
                        disabled={deleteBusy && deleteTarget?.deviceId === device.deviceId}
                      >
                        <FaTrashAlt />
                        Delete TV
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <TvEmptyState
              title="No matching devices"
              copy="Paired TVs will show here. Search and filter work client-side across device identity and location fields."
            />
          )}
        </div>
      </div>

      {approveTarget ? (
        <ModalShell title={`Approve Pairing ${approveTarget.pairingCode}`} onClose={() => setApproveTarget(null)}>
          <form
            className="tv-display-modal-form"
            onSubmit={(event) => {
              event.preventDefault();
              runAction(
                `approve:${approveTarget.id}`,
                async () => {
                  await approveTvPairing({
                    pairingCode: approveTarget.pairingCode,
                    ...approveForm,
                  });
                  setApproveTarget(null);
                },
                `Pairing ${approveTarget.pairingCode} approved and device registration created.`
              );
            }}
          >
            <FieldGroup form={approveForm} setForm={setApproveForm} />
            <div className="tv-display-modal-actions">
              <button type="button" className="tv-display-action-btn" onClick={() => setApproveTarget(null)}>
                Cancel
              </button>
              <button type="submit" className="tv-display-action-btn is-primary" disabled={busyKey === `approve:${approveTarget.id}`}>
                Approve & Pair
              </button>
            </div>
          </form>
        </ModalShell>
      ) : null}

      {editTarget ? (
        <ModalShell title={`Edit ${editTarget.deviceName || editTarget.deviceId}`} onClose={() => setEditTarget(null)}>
          <form
            className="tv-display-modal-form"
            onSubmit={(event) => {
              event.preventDefault();
              runAction(
                `edit:${editTarget.id}`,
                async () => {
                  await updateTvDeviceDetails({
                    deviceId: editTarget.deviceId,
                    ...editForm,
                  });
                  setEditTarget(null);
                },
                `Device ${editTarget.deviceId} details updated.`
              );
            }}
          >
            <FieldGroup form={editForm} setForm={setEditForm} />
            <div className="tv-display-modal-actions">
              <button type="button" className="tv-display-action-btn" onClick={() => setEditTarget(null)}>
                Cancel
              </button>
              <button type="submit" className="tv-display-action-btn is-primary" disabled={busyKey === `edit:${editTarget.id}`}>
                Save Details
              </button>
            </div>
          </form>
        </ModalShell>
      ) : null}

      {viewTarget ? (
        <ModalShell title={viewTarget.deviceName || viewTarget.deviceId} onClose={() => setViewTarget(null)}>
          <div className="tv-display-detail-grid">
            <DetailRow label="Device ID" value={viewTarget.deviceId} />
            <DetailRow label="Auth UID" value={viewTarget.authUid || "Pending"} />
            <DetailRow label="Device Name" value={viewTarget.deviceName || "Unnamed"} />
            <DetailRow label="Pairing Status" value={viewTarget.pairingStatus} />
            <DetailRow label="Enabled Status" value={viewTarget.enabled === false ? "Disabled" : "Enabled"} />
            <DetailRow label="Online Status" value={viewTarget.computedStatus} />
            <DetailRow label="Country" value={viewTarget.country || "-"} />
            <DetailRow label="State" value={viewTarget.state || "-"} />
            <DetailRow label="City" value={viewTarget.city || "-"} />
            <DetailRow label="Manufacturer" value={viewTarget.manufacturer || "-"} />
            <DetailRow label="Model" value={viewTarget.model || "-"} />
            <DetailRow label="Android Version" value={viewTarget.androidVersion || "-"} />
            <DetailRow label="App Version" value={viewTarget.appVersion || "-"} />
            <DetailRow label="Assigned Playlist" value={viewTarget.assignedPlaylistName || viewTarget.assignedPlaylistId || "Not Assigned"} />
            <DetailRow label="Published Version" value={viewTarget.lastPublishedVersion ? `Version ${viewTarget.lastPublishedVersion}` : "Never"} />
            <DetailRow label="Applied Version" value={viewTarget.lastAppliedVersion != null ? `Version ${viewTarget.lastAppliedVersion}` : "Waiting"} />
            <DetailRow label="Delivery Status" value={getComputedDeliveryStatus(viewTarget)} />
            <DetailRow label="Signage Mode" value={viewTarget.signageMode || "STANDARD"} />
            <DetailRow label="Device Owner" value={viewTarget.isDeviceOwner ? "Yes" : "No"} />
            <DetailRow label="Kiosk" value={viewTarget.isLockTaskActive ? "Active" : "Inactive"} />
            <DetailRow label="Home Mode" value={viewTarget.isManagedHome ? "Active" : "Inactive"} />
            <DetailRow label="Playback Health" value={viewTarget.playbackHealth || "STARTING"} />
            <DetailRow label="Screen Policy" value={viewTarget.screenPolicy || "-"} />
            <DetailRow label="Registered At" value={formatDateTime(viewTarget.registeredAt)} />
            <DetailRow label="Paired At" value={formatDateTime(viewTarget.pairedAt)} />
            <DetailRow label="Last Seen" value={formatDateTime(viewTarget.lastSeenAt)} />
            <DetailRow label="Revoked At" value={formatDateTime(viewTarget.revokedAt)} />
            <DetailRow label="Last Sync Error" value={viewTarget.lastSyncError || "-"} />
          </div>
        </ModalShell>
      ) : null}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Delete this TV permanently?"
        message={deleteTarget ? `${deleteTarget.deviceName || deleteTarget.deviceId} will be removed from the realtime device registry. Its Firebase TV identity will be deleted and this APK will need to be paired again. Playlists, media and publication history will remain safe.` : ""}
        confirmText="Delete TV Permanently"
        cancelText="Cancel"
        loading={deleteBusy}
        error={deleteError}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleteTarget(null);
          setDeleteError("");
        }}
        onConfirm={confirmDeleteDevice}
      />
    </section>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="tv-display-media-kpi-card">
      <div className="tv-display-media-kpi-label">{label}</div>
      <div className="tv-display-media-kpi-value">{value}</div>
    </div>
  );
}

function FieldGroup({ form, setForm }) {
  return (
    <div className="tv-display-form-grid">
      <label className="tv-display-field">
        <span className="tv-display-field-label">Device Name *</span>
        <input
          value={form.deviceName}
          onChange={(event) => setForm((current) => ({ ...current, deviceName: event.target.value }))}
          maxLength={120}
          required
        />
      </label>
      <label className="tv-display-field">
        <span className="tv-display-field-label">Country</span>
        <input
          value={form.country}
          onChange={(event) => setForm((current) => ({ ...current, country: event.target.value }))}
          maxLength={120}
        />
      </label>
      <label className="tv-display-field">
        <span className="tv-display-field-label">State / Region</span>
        <input
          value={form.state}
          onChange={(event) => setForm((current) => ({ ...current, state: event.target.value }))}
          maxLength={120}
        />
      </label>
      <label className="tv-display-field">
        <span className="tv-display-field-label">City</span>
        <input
          value={form.city}
          onChange={(event) => setForm((current) => ({ ...current, city: event.target.value }))}
          maxLength={120}
        />
      </label>
      <label className="tv-display-field is-span-2">
        <span className="tv-display-field-label">Notes</span>
        <textarea
          value={form.notes}
          onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
          rows={4}
          maxLength={500}
        />
      </label>
    </div>
  );
}

function DeviceMeta({ label, value, icon = null }) {
  return (
    <div className="tv-display-device-meta-card">
      <div className="tv-display-device-meta-label">
        {icon}
        <span>{label}</span>
      </div>
      <div className="tv-display-device-meta-value">{value}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="tv-display-detail-row">
      <span className="tv-display-detail-label">{label}</span>
      <span className="tv-display-detail-value">{value}</span>
    </div>
  );
}

function PairingLookupMessage({ pairing }) {
  if (pairing.status === TV_PAIRING_STATUSES.REQUESTED) {
    return <span>This TV is waiting for approval. You can approve it below.</span>;
  }
  if (pairing.status === TV_PAIRING_STATUSES.EXPIRED) {
    return <span>This pairing code has expired. Request a new code on the TV.</span>;
  }
  if (pairing.status === TV_PAIRING_STATUSES.COMPLETED) {
    return <span>This pairing code has already been used.</span>;
  }
  if (pairing.status === TV_PAIRING_STATUSES.REJECTED) {
    return <span>This pairing request was rejected and cannot be reused.</span>;
  }
  return <span>This pairing request has already been processed.</span>;
}

function ModalShell({ title, children, onClose }) {
  return (
    <div className="tv-display-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="tv-display-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="tv-display-modal-head">
          <h3>{title}</h3>
          <button type="button" className="tv-display-modal-close" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function StatusChip({ value, tone = "" }) {
  const chipTone = tone || String(value || "").toLowerCase();
  return <span className={`tv-display-chip is-${chipTone}`}>{value}</span>;
}

function formatLocation(device) {
  const value = [device.city, device.state, device.country].filter(Boolean).join(", ");
  return value || "Not provided";
}

function formatHardware(device) {
  const value = [device.manufacturer, device.model].filter(Boolean).join(" / ");
  return value || "Unknown";
}

function formatDateTime(value) {
  const date = toDate(value);
  if (!date) return "Not yet";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function formatRelative(value) {
  const date = toDate(value);
  if (!date) return "Unknown";

  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  if (Math.abs(diffMinutes) < 1) return "just now";
  if (Math.abs(diffMinutes) < 60) {
    return `${Math.abs(diffMinutes)} minute${Math.abs(diffMinutes) === 1 ? "" : "s"} ${diffMinutes < 0 ? "ago" : "from now"}`;
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) {
    return `${Math.abs(diffHours)} hour${Math.abs(diffHours) === 1 ? "" : "s"} ${diffHours < 0 ? "ago" : "from now"}`;
  }

  const diffDays = Math.round(diffHours / 24);
  return `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? "" : "s"} ${diffDays < 0 ? "ago" : "from now"}`;
}

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function resolveActionError(error) {
  const code = String(error?.code || "").toLowerCase();
  if (code.includes("permission-denied")) return "You do not have permission to manage TV pairing or devices.";
  if (code.includes("not-found")) return "The requested TV or pairing record no longer exists.";
  if (code.includes("failed-precondition")) return error?.message || "This action is no longer allowed in the current device state.";
  return error?.message || "The TV device action failed. Please try again.";
}
